import { Box, styled } from '@mui/material';
import { CardPosition } from '../cards-with-layout';
import { Card } from '@/screens/authenticated/types';
import React, { FC } from 'react';
import { useThrowCard } from '../../context';
import { useThrowCardAnimation } from '../../hooks';
import { DeckCard } from '../deck-card';
import { clampCardLeft } from '../hand-layout';
import { useLandscapeMode } from '@/shared/hooks';
import { cardLabel } from '@/screens/guest/lib/turn';
import type { SelectedCard } from '../../use-play-card';

const ConfirmCardBoxStyled = styled(Box, {
  shouldForwardProp: prop => prop !== 'pos' && prop !== 'isVerticalMode',
})<{ pos: CardPosition; isVerticalMode: boolean }>(
  ({ theme, pos, isVerticalMode }) => ({
    position: 'absolute',
    top: `calc(${pos.top}px - env(safe-area-inset-bottom) - ${isVerticalMode ? '100px' : '70px'})`,
    left: 0,
    width: '100%',
    paddingBottom: theme.spacing(2),
    zIndex: 1000,
  })
);

interface ConfirmCardBoxProps {
  card: Card;
  setSelectedCard: React.Dispatch<React.SetStateAction<SelectedCard>>;
  pos: CardPosition;
}

/**
 * The card being played, raised above the hand while POST /in-play is in
 * flight and spun towards the table once the server accepts it. It is purely
 * presentational: the play is submitted by the single hand click
 * (see usePlayCard), so this overlay has no click / confirm action.
 */
export const ConfirmCardBox: FC<ConfirmCardBoxProps> = ({
  card,
  setSelectedCard,
  pos,
}) => {
  const { thrownCard } = useThrowCard();
  const animateProps = useThrowCardAnimation(card, setSelectedCard);
  const isLandscape = useLandscapeMode();

  return (
    <ConfirmCardBoxStyled pos={pos} isVerticalMode={!isLandscape}>
      <Box
        className="st-confirm-card"
        role="status"
        aria-live="polite"
        aria-label={`Playing ${cardLabel(card)}`}
        sx={{
          position: 'absolute',
          // A card tapped while partly scrolled out of the mobile hand strip
          // reports an off-screen left edge; keep the raised card visible.
          left: clampCardLeft(
            pos.left,
            pos.right - pos.left > 0 ? pos.right - pos.left : 60,
            window.innerWidth
          ),
        }}
      >
        <DeckCard
          card={card}
          motionDivProps={thrownCard ? animateProps : undefined}
        />
      </Box>
    </ConfirmCardBoxStyled>
  );
};
